import { useState } from 'react';
import styled from 'styled-components';
import gift from '../assets/images/gift.svg';
import close from '../assets/icons/close.svg';
import { StyledParagraph, StyledParagraphLarge, StyledParagraphMedium } from '../common/typography/Typography';
import { useTranslations } from '../localisation/translateText';
import { translations, translationsType } from './translations/index';
import { breakPointLG } from '../globalConfig/styling/breakPoints';
import Modal from 'react-modal';
import giftIcon from '../assets/icons/gift-icon.svg';

const LabelContainer = styled.div`
  background-color: ${(props) => props.theme.color};
  display: flex;
  flex-direction: column;
`;

const LabelContainerTop = styled.div`
  padding: 14px 22px 20px;
  background-color: ${(props) => props.theme.color};
  display: flex;
  position: relative;
  align-items: center;
`;

const GiftImage = styled.img`
  background-color: ${(props) => props.theme.color};
  position: absolute;
  right: 50%;
  bottom: -10px;
  height: 20px;
  width: 20px;

  @media (min-width: ${breakPointLG}px) {
    position: static;
    height: 40px;
    width: 40px;
  }
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${(props) => props.theme.color};
  width: 100%;

  @media (min-width: ${breakPointLG}px) {
    margin-left: 22px;
    align-items: flex-start;
  }
`;

const LabelContainerBottom = styled.div`
  display: flex;
  width: 100%;
  border-top: 1px solid ${(props) => props.theme.invertColor};
`;

const MessageLinkContent = styled.div`
  display: flex;
  justify-content: center;
  padding: 9px 22px 15px;
  background-color: ${(props) => props.theme.color};
  width: 100%;

  @media (min-width: ${breakPointLG}px) {
    justify-content: flex-start;
  }
`;

const LabelLink = styled.button`
  border: none;
  cursor: pointer;
  background: transparent;
  text-decoration: underline;
  color: ${(props) => props.theme.invertColor};
  justify-content: center;
`;

const SenderText = styled.h3`
  font-size: ${(props) => props.theme.fontSizeSubTitle};
  background: transparent;
  text-transform: uppercase;
  color: ${(props) => props.theme.invertColor};
  text-align: left;
  font-weight: 600;
`;

const GiftIcon = styled.img`
  background-color: transparent;
  height: ${(props) => props.theme.fontSizeTitle};
  margin-right: 5px;
`;

const ModalContent = styled.div`
  background-color: white;

  & div {
    background-color: white;
  }
`;

const CloseButton = styled.button`
  border: none;
  cursor: pointer;
  background: transparent;
`;
const CloseIcon = styled.img`
  background-color: transparent;
  height: ${(props) => props.theme.fontSizeNormal};
  margin-right: 5px;
  opacity: 0.5;
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 30px 20px 30px;
  gap: 20px;
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: end;
`;

interface GiftLabelProps {
  giftSender: string | null;
  giftMessage?: string;
  brandName: string;
}

export const GiftLabel = ({ giftSender, giftMessage, brandName }: GiftLabelProps): JSX.Element => {
  const { translationsContent, getTranslationWithValues } = useTranslations<translationsType>(translations);
  const [modalIsOpen, setIsOpen] = useState(false);

  return (
    <LabelContainer>
      <LabelContainerTop>
        <GiftImage src={gift} alt={translationsContent.giftIconAlt} />
        <TextContainer>
          {giftSender && <SenderText>{giftSender}</SenderText>}
          <StyledParagraph center invertColor uppercase>
            {getTranslationWithValues(translationsContent.giftReceivedSentence, [brandName])}
          </StyledParagraph>
        </TextContainer>
      </LabelContainerTop>
      {giftMessage && giftMessage !== '' ? (
        <LabelContainerBottom>
          <MessageLinkContent>
            <LabelLink onClick={() => setIsOpen(true)}>{translationsContent.readMessageLink}</LabelLink>
            <Modal
              isOpen={modalIsOpen}
              onRequestClose={() => setIsOpen(false)}
              contentLabel={translationsContent.giftMessageModalContentLabel}
              style={{
                content: {
                  width: '70%',
                  height: 'fit-content',
                  padding: 20,
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                },
              }}
            >
              <ModalContent>
                <ActionBar>
                  <CloseButton onClick={() => setIsOpen(false)}>
                    <CloseIcon src={close} alt="Close button" />
                  </CloseButton>
                </ActionBar>
                <TextContent>
                  <StyledParagraphLarge>{giftMessage}</StyledParagraphLarge>
                  <StyledParagraphMedium>
                    <GiftIcon src={giftIcon} alt="Gift icon" />
                  </StyledParagraphMedium>
                </TextContent>
              </ModalContent>
            </Modal>
          </MessageLinkContent>
        </LabelContainerBottom>
      ) : null}
    </LabelContainer>
  );
};
